<template>
  <v-card class="elevation-2" style="max-width: 100%;">
    <v-card-title class="pb-2 pt-2">
      <span style="font-size: 12px;" class="primary--text">Cash Flow</span>
      <v-tooltip right>
        <v-icon small color="#ccc" slot="activator" class="pl-2">help</v-icon>
        <span
          >Amount of money moving in and out in your bisness. The real cash flow
          is based on bank transactions while the theorical cash flow is based
          on registered invoices.</span
        >
      </v-tooltip>
      <v-spacer></v-spacer>
      <v-btn small icon flat @click="lastYear">
        <v-icon>keyboard_arrow_left</v-icon>
      </v-btn>
      <div>{{ currentDate | friendlyDate }}</div>
      <v-btn small icon flat @click="nextYear">
        <v-icon>keyboard_arrow_right</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text class="pt-0">
      <v-layout row wrap>
        <v-flex class="text-xs-center">
          <span class="subheadings" style="color: #333;"
            >Cash as on {{ oneYearAgo | friendlyDate }}</span
          >
          <br />
          <strong>{{ inCurrency(currencyCode, totalOneYearAgo) }}</strong>
        </v-flex>

        <v-flex class="text-xs-center">
          <span class="subheadings success--text">Incoming</span>
          <br />
          <strong>{{ inCurrency(currencyCode, totalIncomming) }}</strong>
        </v-flex>

        <v-flex class="text-xs-center">
          <span class="subheadings error--text">Outgoing</span>
          <br />
          <strong>{{ inCurrency(currencyCode, totalOutcomming) }}</strong>
        </v-flex>

        <v-flex class="text-xs-center">
          <span class="subheadings blue--text"
            >Cash as on {{ now | friendlyDate }}</span
          >
          <br />
          <strong>{{ inCurrency(currencyCode, totalNow) }}</strong>
        </v-flex>
      </v-layout>
      <apexchart
        :key="refresh"
        width="100%"
        height="280px"
        :options="options"
        :series="series"
      ></apexchart>
    </v-card-text>
  </v-card>
</template>

<script>
import apexchart from "vue-apexcharts";
import currency from "../../../mixins/currency";
import date from "../../../mixins/date";
import accountingFilters from "../../../mixins/accountingFilters";
import { mapGetters } from "vuex";
import BANK_INVOICE_REPORT from "../../../graphql/bank_invoices/report.gql";
import BANK_TRANSACTION_REPORT from "../../../graphql/bank_transactions/report.gql";

export default {
  name: "accountingClashFlow",
  components: { apexchart },
  mixins: [accountingFilters, currency, date],
  data() {
    return {
      currentDate: new Date(),
      bankTransactionReports: [],
      bankInvoiceReports: [],
      refresh: 0,
      totalIncomming: 0,
      totalOutcomming: 0,
      totalOneYearAgo: 0,
      totalNow: 0,
      options: {
        colors: [
          "#2196f3",
          "#ff5252",
          "#4caf50",
          "#2196f3",
          "#ff5252",
          "#4caf50",
        ],
        stroke: {
          width: [7, 5, 5, 7, 5, 5],
          curve: "straight",
          dashArray: [0, 0, 0, 5, 5, 5],
        },
        chart: {
          zoom: {
            enabled: false,
          },
          currencySymbol: "",
          id: "cashflow",
          toolbar: {
            show: false,
          },
        },
        xaxis: {},
        yaxis: {
          labels: {
            formatter: function (value, opt) {
              if (typeof opt != "number")
                return opt.w.config.chart.currencySymbol + " " + value;

              return value;
            },
          },
        },
      },
      series: [
        {
          name: "Real Cash flow",
          data: [],
        },
        {
          name: "Transaction Debit amount",
          data: [],
        },
        {
          name: "Transaction Credit amount",
          data: [],
        },
        {
          name: "Theorical Cash Flow",
          data: [],
        },
        {
          name: "Invoice Debit amount",
          data: [],
        },
        {
          name: "Invoice Credit amount",
          data: [],
        },
      ],
    };
  },
  computed: {
    ...mapGetters("commonStore", ["subsidiaries"]),
    inlineBalance() {
      return this.$vuetify.breakpoint.sm || this.$vuetify.breakpoint.xs
        ? "display: inline-grid; padding-right:50px;"
        : "display: grid; width:auto;";
    },
    currencySymbol() {
      return this.getSymbolFromCode(this.currencyCode);
    },
    bankTransactionReportsData() {
      var ret = this.bankTransactionReports.filter(
        (e) => e.currency_id === this.currencyId
      );
      if (!this.subsidiaryId) return ret;
      var company = this.subsidiaries.find((e) => e.id === this.subsidiaryId);
      let bank_informations = company.subsidiary.bank_informations.map(
        (f) => f.id
      );

      ret = ret.filter(
        (e) => bank_informations.indexOf(e.bank_information_id) >= 0
      );
      return ret;
    },
    bankInvoiceReportsData() {
      var ret = this.bankInvoiceReports.filter(
        (e) => e.currency_id === this.currencyId
      );
      if (!this.subsidiaryId) return ret;

      return ret.filter((e) => e.subsidiary_company_id == this.subsidiaryId);
    },
    currentMonthIndex() {
      return this.currentDate.getMonth();
    },
    monthsFilter() {
      let today = this.currentDate;
      let currentMonth = today.getMonth() + 1;
      let currentYear = today.getYear() + 1900;
      return `((month <= ${currentMonth} && year == ${currentYear}) ||
               (month >  ${currentMonth} && year == ${currentYear - 1}))`;
    },
  },
  methods: {
    initCategories() {
      var months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      var tmp = [];
      for (
        var i = this.currentMonthIndex + 1;
        i < this.currentMonthIndex + 13;
        i++
      )
        tmp.push(months[i % 12]);

      this.options.xaxis.categories = tmp;
    },
    initChart() {
      this.totalIncomming = 0;
      this.totalOutcomming = 0;
      this.totalOneYearAgo = 0;
      this.totalNow = 0;
      this.options.chart.currencySymbol = this.currencySymbol;
      var transactionCashFlow = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      var transactionCredit = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      var transactionDebit = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      var invoiceCashFlow = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      var invoiceCredit = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      var invoiceDebit = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

      this.bankTransactionReportsData.forEach((e) => {
        let month = e.month;
        let index = (11 - this.currentMonthIndex + month) % 12;
        if (e.operation_type === "credit") {
          transactionCashFlow[index] += e.total_amount;
          transactionCredit[index] += e.total_amount;
          this.totalIncomming += e.total_amount;
        } else {
          transactionCashFlow[index] -= e.total_amount;
          transactionDebit[index] += e.total_amount;
          this.totalOutcomming += e.total_amount;
        }
      });

      this.bankInvoiceReportsData.forEach((e) => {
        let month = e.month;
        let index = (11 - this.currentMonthIndex + month) % 12;
        if (e.operation_type === "credit") {
          invoiceCashFlow[index] += e.total_amount;
          invoiceCredit[index] += e.total_amount;
        } else {
          invoiceCashFlow[index] -= e.total_amount;
          invoiceDebit[index] += e.total_amount;
        }
      });

      for (var i = 1; i < transactionCashFlow.length; i++) {
        transactionCashFlow[i] += transactionCashFlow[i - 1];
        invoiceCashFlow[i] += invoiceCashFlow[i - 1];
      }
      this.totalOneYearAgo = transactionCashFlow[0];
      this.totalNow = transactionCashFlow[transactionCashFlow.length - 1];
      this.series[0].data = transactionCashFlow;
      this.series[1].data = transactionDebit;
      this.series[2].data = transactionCredit;
      this.series[3].data = invoiceCashFlow;
      this.series[4].data = invoiceDebit;
      this.series[5].data = invoiceCredit;
      this.refresh++;
    },
    fetchTransactionReport() {
      this.sendQuery(BANK_TRANSACTION_REPORT, {
        filter: this.monthsFilter,
      }).then((data) => {
        this.bankTransactionReports = data.bank_transaction_reports;
      });
    },
    fetchInvoiceReport() {
      this.sendQuery(BANK_INVOICE_REPORT, {
        filter: this.monthsFilter,
      }).then((data) => {
        this.bankInvoiceReports = data.bank_invoice_reports;
      });
    },
    nextYear() {
      var newYear = this.currentDate.getFullYear() + 1;
      this.currentDate = new Date(this.currentDate.setFullYear(newYear));
      this.fetchTransactionReport();
      this.fetchInvoiceReport();
    },
    lastYear() {
      var newYear = this.currentDate.getFullYear() - 1;
      this.currentDate = new Date(this.currentDate.setFullYear(newYear));
      this.fetchTransactionReport();
      this.fetchInvoiceReport();
    },
  },
  created() {
    this.initCategories();
    this.fetchTransactionReport();
    this.fetchInvoiceReport();
  },
  watch: {
    currencyId() {
      this.initChart();
    },
    subsidiaryId() {
      this.initChart();
    },
    bankTransactionReports() {
      this.initChart();
    },
  },
};
</script>

<style scoped>
.cashflowBalance {
  padding-right: 17px;
  margin-bottom: 20px;
  box-sizing: border-box;
}

.info_tooltip {
  cursor: pointer;
}
</style>
